import { useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getPageLoadState,
  resetPageLoad,
  subscribePageLoad,
} from '@/lib/pageLoadSignal';

/**
 * ページ移動（読み込み/移動）時に画面全体を白くフェードで覆うオーバーレイ。
 *
 * 以前は固定時間（0.72秒）で「覆う→消す」を再生していたが、読み込みが遅いページでは
 * 白が消えたあとに中身が動いて見えることがあった。
 * 次に「DOM の動きが止まったら完了」と推測する方式にしたが、推測は外れることがあった
 * （遅れて走る取得など）。
 *
 * 幕のかぶせ方（重要）:
 * - 幕は「新ページが描画される前（ペイント前）」に用意する。useEffect はペイント後に
 *   走るため、先に新ページが一瞬見えてから幕のフェードが始まり、
 *   「一瞬表示 → あとからフェード」という順番になっていた。useLayoutEffect にして
 *   同じフレームで幕を乗せることで、この一瞬の表示をなくす。
 * - かぶせるときは即時（フェードインさせない）。明けるときだけフェードアウトする。
 *
 * そこで今は、実際のデータ取得そのものを合図に使う：
 * - 白で覆ったまま、Supabase への通信（データ取得）が 0 本に戻るのを待つ
 * - あわせて DOM の変化・レイアウト変化・フォント読み込みが SETTLE_MS 落ち着くのを待つ
 * - 両方が満たされたら「読み込み完了」としてフェードアウトを開始する
 *
 * 固まらないよう上限（MAX_WAIT_MS）で必ず解除し、速いページで一瞬で消えないよう
 * 最低表示（MIN_VISIBLE_MS）を確保する。
 * 白で覆っている間は「処理中」と分かるよう小さなローディング表示を重ねる。
 *
 * - アプリ起動時（初回読み込み）も表示する
 * - ルート（pathname/search）が変わるたびに再生する
 * - ただの見た目なのでクリックは通す（pointer-events-none）
 */

/** カバーのフェードイン完了後、最低これだけは表示する（一瞬で消えるのを防ぐ） */
const MIN_VISIBLE_MS = 260;
/** コンテンツの動き／通信が落ち着いてから「完了」と判定するまでの猶予 */
const SETTLE_MS = 160;
/** 新ページのデータ取得が始まるのを待つ猶予（これ以内は「まだ始まっていない」扱い） */
const KICKOFF_MS = 220;
/** どんなに重いページでも必ずこの時間で解除する上限 */
const MAX_WAIT_MS = 1600;
/** フェードアウトの長さ（index.css の transition と揃える） */
const REVEAL_MS = 320;

export default function PageTransitionOverlay() {
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [visible, setVisible] = useState(false);

  useLayoutEffect(() => {
    // この白フェードはアプリ共通の演出として常時再生する。
    // 以前は OS の「視覚効果/アニメーションを減らす」（prefers-reduced-motion: reduce）を
    // 検知すると演出をスキップしていたが、軽減設定の端末ではページ遷移のフェードが
    // 一切出ず（ログアウト以外が全滅する）端末によって見た目がバラついていた。
    // 演出は opacity のみの控えめなものなので、設定に関わらず再生する。

    let cancelled = false;
    const timers: number[] = [];
    let stopWatching: () => void = () => {};
    const startedAt = performance.now();

    // 前のページの通信カウントを引き継がないよう初期化する
    resetPageLoad();

    // 新ページが描画される「前」（ペイント前）に、白で覆った状態を用意する。
    // useEffect（ペイント後）だと、先に新ページが一瞬見えてから幕のフェードが
    // 始まってしまう。useLayoutEffect なら同じフレームで幕が乗るため、
    // 「一瞬表示 → あとからフェード」が起きない。
    // 幕はかぶせるときは即時（フェードインさせない）。明けるときだけフェードする
    // （index.css の .screen-fade-overlay.is-visible と対応）。
    setActive(true);
    setVisible(true);

    // 完了したらフェードアウトして解除する
    const reveal = () => {
      if (cancelled) return;
      const elapsed = performance.now() - startedAt;
      const wait = Math.max(0, MIN_VISIBLE_MS - elapsed);
      timers.push(
        window.setTimeout(() => {
          if (cancelled) return;
          setVisible(false);
          timers.push(
            window.setTimeout(() => {
              if (!cancelled) setActive(false);
            }, REVEAL_MS),
          );
        }, wait),
      );
    };

    let settled = false;
    let settleTimer: number | undefined;

    const finish = () => {
      if (settled || cancelled) return;
      settled = true;
      stopWatching();
      reveal();
    };

    const isIdle = () => {
      const s = getPageLoadState();
      // データ取得（Supabase 通信）と、遅延読み込みページの JS チャンク取得の
      // どちらも終わっていて初めて「落ち着いた」と見なす。チャンク取得中は
      // ページの中身がまだ無いので、ここで明けるとコンテンツより先にフェードが
      // 終わってしまう（特に通信が遅い環境）。
      return s.pending === 0 && s.routePending === 0;
    };
    const hasDataStarted = () => getPageLoadState().started;

    // 落ち着きを検知するたびに呼ぶ。通信が0で、動きも止まったら完了。
    const armSettle = () => {
      if (settled || cancelled) return;
      if (settleTimer) window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        if (settled || cancelled) return;
        // まだ通信中なら待つ（0に戻った時点で購読側から再判定される）
        if (!isIdle()) return;
        // まだデータ取得が始まっていない場合、始まる猶予を確保してから判定する
        if (!hasDataStarted() && performance.now() - startedAt < KICKOFF_MS) {
          armSettle();
          return;
        }
        finish();
      }, SETTLE_MS);
    };

    // データ取得の増減を監視：通信中は待機、0に戻ったら判定
    const unsubscribe = subscribePageLoad(() => {
      if (settled || cancelled) return;
      if (!isIdle()) {
        if (settleTimer) window.clearTimeout(settleTimer);
        return;
      }
      armSettle();
    });

    // DOM の変化（データ取得後の再描画など）を監視
    const mutation = new MutationObserver(armSettle);
    mutation.observe(document.body, { childList: true, subtree: true, characterData: true });

    // レイアウトの変化（画像読み込み等で高さが動く）を監視
    const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(armSettle) : undefined;
    resize?.observe(document.body);

    // Web フォントの読み込み完了も待つ
    if (document.fonts?.ready) {
      document.fonts.ready.then(armSettle).catch(() => {});
    }

    // 何があっても解除する上限タイマー
    const maxTimer = window.setTimeout(finish, MAX_WAIT_MS);

    stopWatching = () => {
      unsubscribe();
      mutation.disconnect();
      resize?.disconnect();
      if (settleTimer) window.clearTimeout(settleTimer);
      window.clearTimeout(maxTimer);
    };

    // 変化が無い場合に備えて最初の判定を開始しておく
    armSettle();

    return () => {
      cancelled = true;
      timers.forEach((t) => window.clearTimeout(t));
      stopWatching();
    };
  }, [location.key]);

  if (!active) {
    return null;
  }

  return (
    <div
      className={`screen-fade-overlay pointer-events-none fixed inset-0 z-[190] flex items-center justify-center${
        visible ? ' is-visible' : ''
      }`}
      aria-hidden="true"
    >
      <div className="screen-fade-spinner flex flex-col items-center">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin" />
        <p className="mt-3 text-xs tracking-wide text-gray-400">読み込み中...</p>
      </div>
    </div>
  );
}